import type { GetServerSidePropsContext, NextPage } from 'next';
import { useEffect, useState } from 'react';
import Link from 'next/link';

import { useForm } from 'react-hook-form';

import toast, { Toaster } from 'react-hot-toast';

import { ContactsApiService, WebAppApiService } from 'services';

import { PagesType } from 'common/pages-type';

import Layout from '@/components/layout/layout';

import { IContactModel, IWebAppModel } from 'models';

import styles from './contact.module.scss';
import { MixpanelTrackingService } from '@/services/mixpanel-services';

interface IContactPageProps {
  webApp: IWebAppModel;
  preview: boolean;
}

const ContactPage: NextPage<IContactPageProps> = ({ webApp, preview }) => {
  useEffect(() => {
    MixpanelTrackingService.getInstance().pageViewed();
  }, []);

  const { register, formState, reset, handleSubmit, getValues } =
    useForm<IContactModel>({
      criteriaMode: 'all',
      mode: 'onChange',
    });

  const [showLoader, setShowLoader] = useState<boolean>(false);

  const onSubmit = async (contact: IContactModel) => {
    setShowLoader(true);
    const apiService = new ContactsApiService();
    let success = await apiService.createContact(contact);
    setShowLoader(false);
    if (success) {
      // Show toast success
      toast.success('Demande de contact effectué avec succès.', {
        duration: 5000,
        position: 'bottom-center',
      });
      // Reset form
      reset();
    } else {
      // Show toast error
      toast.error('Erreur lors de la demande de contact. Veuillez réessayer.', {
        duration: 5000,
        position: 'bottom-center',
      });
    }
  };

  const handleLatinCharacters = (
    event:
      | React.KeyboardEvent<HTMLInputElement>
      | React.ClipboardEvent<HTMLInputElement>,
    value: string,
  ) => {
    if (!/([0-9+])/gi.test(value)) {
      event.preventDefault();
    }
  };

  return (
    <Layout
      preview={preview}
      origin={PagesType.Contact}
      headerImageUrl={webApp.headerImageUrl}
      logoUrl={webApp.logoUrl}
      stravaUrl={webApp.stravaUrl}
      facebookUrl={webApp.facebookUrl}
      instagramUrl={webApp.instagramUrl}
      title={webApp.siteName}
      description={webApp.description}>
      <div className={styles.container}>
        <h1 className={styles.title}>Contact</h1>
        <form key={1} onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="form-item" style={{ flex: 50 }}>
              <label htmlFor="lastName">
                Prénom
                <span className="required">*</span> :
              </label>
              <input
                type="text"
                id="lastName"
                className={formState.errors.lastName && 'is-error'}
                {...register('lastName', {
                  required: {
                    value: true,
                    message: 'Champ obligatoire',
                  },
                })}
              />
              {formState.errors.lastName && (
                <p className="is-error">{formState.errors.lastName.message}</p>
              )}
            </div>
            <div className="form-item" style={{ flex: 50 }}>
              <label htmlFor="firstName">
                Nom
                <span className="required">*</span> :
              </label>
              <input
                type="text"
                id="firstName"
                className={formState.errors.firstName && 'is-error'}
                {...register('firstName', {
                  required: {
                    value: true,
                    message: 'Champ obligatoire',
                  },
                })}
              />
              {formState.errors.firstName && (
                <p className="is-error">{formState.errors.firstName.message}</p>
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-item" style={{ flex: 50 }}>
              <label htmlFor="email">
                Email
                <span className="required">*</span> :
              </label>
              <input
                type="text"
                id="email"
                className={formState.errors.email && 'is-error'}
                {...register('email', {
                  required: {
                    value: true,
                    message: 'Champ obligatoire',
                  },
                  pattern: {
                    value:
                      /^(([^<>()\[\]\\.,;:\s@!#$%\^&"]+(\.[^<>()\[\]\\.,;:\s@!#$%\^&"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: `Format d'email invalide`,
                  },
                  maxLength: {
                    value: 50,
                    message: 'Longueur max 50 caractères',
                  },
                })}
              />
              {formState.errors.email && (
                <p className="is-error">{formState.errors.email.message}</p>
              )}
            </div>
            <div className="form-item" style={{ flex: 50 }}>
              <label htmlFor="phone">Téléphone :</label>
              <input
                type="text"
                id="phone"
                className={formState.errors.phone && 'is-error'}
                {...register('phone', {
                  maxLength: {
                    value: 12,
                    message: 'Caractères autorisés maximum 12',
                  },
                })}
                onKeyPress={(e) => handleLatinCharacters(e, e.key)}
                onPaste={(e) =>
                  handleLatinCharacters(
                    e,
                    e.clipboardData.getData('text/plain'),
                  )
                }
              />
              {formState.errors.phone && (
                <p className="is-error">{formState.errors.phone.message}</p>
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-item">
              <label htmlFor="email">
                Votre message
                <span className="required">*</span> :
              </label>
              <textarea
                id="message"
                className={formState.errors.message && 'is-error'}
                {...register('message', {
                  required: {
                    value: true,
                    message: 'Champ obligatoire',
                  },
                })}
              />
              {formState.errors.message && (
                <p className="is-error">{formState.errors.message.message}</p>
              )}
            </div>
          </div>
          <div className="row">
            <div className="form-item">
              <input
                type="checkbox"
                id="acceptExploitInfo"
                {...register('acceptExploitInfo', {
                  required: {
                    value: true,
                    message: 'Vous devez accepter',
                  },
                })}
              />
              <label
                htmlFor="acceptExploitInfo"
                className={styles['accept-exploit-info']}>
                <span className="required">*</span> En soumettant ce formulaire,
                j&apos;accepte que les informations saisies soient exploitées
                dans le cadre de ma demande d&apos;information
              </label>
              {formState.errors.acceptExploitInfo && (
                <p className="is-error">
                  {formState.errors.acceptExploitInfo?.message}
                </p>
              )}
            </div>
          </div>
          <div className="row">
            <span className="required">*</span>&nbsp;Champs obligatoires
          </div>
          <div className="row">
            <div className={styles.action}>
              <button
                type="submit"
                value="Submit"
                className="primary"
                disabled={showLoader}>
                Envoyer
                {!showLoader ? <></> : <span className="spinner"></span>}
              </button>
            </div>
          </div>
          {/* <div className="row">
            <Link href="/mentions">
              En savoir plus sur la gestion de vos données personnelles et vos
              droits
            </Link>
          </div> */}
        </form>
      </div>
    </Layout>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  try {
    const webAppService = new WebAppApiService();
    const webapp = await webAppService.getWebApp(context);

    return {
      props: {
        webApp: webapp,
        preview: context.preview ?? false,
      } as IContactPageProps,
    };
  } catch {
    return {
      props: {},
    };
  }
};

export default ContactPage;
